import React from 'react';
import { useLinkPreview } from '@/hooks/useLinkPreview';

interface LinkPreviewProps {
  url: string;
}

function getDomain(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

export function LinkPreview({ url }: LinkPreviewProps) {
  const { data, isLoading } = useLinkPreview(url);

  if (isLoading) {
    return (
      <div className="border border-border rounded-lg overflow-hidden mb-4 animate-pulse">
        <div className="h-[140px] bg-muted" />
        <div className="p-3 space-y-2">
          <div className="h-3 w-24 bg-muted rounded" />
          <div className="h-4 w-3/4 bg-muted rounded" />
          <div className="h-3 w-full bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (!data || (!data.title && !data.description && !data.image)) {
    return null;
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="block border border-border rounded-lg overflow-hidden mb-4 hover:bg-muted/50 transition-colors"
    >
      {data.image && (
        <img
          src={data.image}
          alt={data.title || ''}
          className="w-full max-h-[200px] object-cover"
          loading="lazy"
          onError={(e) => {
            // Hide broken images
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      )}
      <div className="p-3">
        {(data.publisher || data.logo) && (
          <div className="flex items-center gap-1.5 mb-1">
            {data.logo && (
              <img
                src={data.logo}
                alt=""
                className="h-3.5 w-3.5 rounded-sm"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            )}
            <span className="text-xs text-muted-foreground">
              {data.publisher || getDomain(url)}
            </span>
          </div>
        )}
        {data.title && (
          <p className="text-sm font-semibold text-foreground line-clamp-2">
            {data.title}
          </p>
        )}
        {data.description && (
          <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
            {data.description}
          </p>
        )}
        {!data.publisher && !data.logo && (
          <p className="text-xs text-muted-foreground mt-1">
            {getDomain(url)}
          </p>
        )}
      </div>
    </a>
  );
}
